import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Popper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import MapIcon from '@mui/icons-material/Map';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import { useTranslation } from 'react-i18next';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { selectAssociatedAssets } from 'store/asset.slice';
import { showNotification } from 'store/notification.slice';
import useAppTheme from 'hooks/useAppTheme';
import useDispatchMode from 'hooks/useDispatchMode';
import { type GradeRecord } from 'views/DispatchImporters/GradeImporterView';
import { LocationMapModal } from 'views/DispatchImporters/LocationMapModal';
import { type LocationRecord } from 'views/DispatchImporters/LocationImporterView';
import DispatchMonitorAPI from 'api/dispatch-monitor.api';
import AssetAPI from 'api/asset.api';
import LocationImporterAPI, {
  type ImportedLocation,
} from 'api/location-importer.api';
import GradeImporterAPI from 'api/grade-importer.api';
import FleetPlanningAPI from 'api/fleet-planning.api';
import StatusCodesAPI from 'api/status-codes.api';
import ManualDispatchAPI from 'api/manual-dispatch.api';
import { type StatusCodeRecord } from 'views/Settings/StatusCodes/types';
import { useOperatorLiveStatus } from 'hooks/useOperatorLiveStatus';
import { useAssetLiveStatus } from 'hooks/useAssetLiveStatus';
import { useSortableRows, type SortValueGetters } from 'hooks/useSortableRows';
import { SortableTh } from 'components/Table/SortableTh';
import { toMaterialCategoryList } from 'utils/helpers/materialCategory';
import ManualExcavatorPanel from './ManualExcavatorPanel';

// ─── Types ──────────────────────────────────────────────────────────────────

interface NearestLocation {
  locationId: string;
  name: string;
  easting: number;
  northing: number;
  distanceMeters: number;
  meta: Record<string, unknown> | null;
}

export interface AssetInfo {
  bumperNumber: string;
  symbotId: string;
  assetType: string;
  meta: Record<string, unknown> | null;
  lat: number;
  lon: number;
  nearestLoading: NearestLocation | null;
  nearestDumping: NearestLocation | null;
  nearestStockpile: NearestLocation | null;
  nearestCrusher: NearestLocation | null;
}

export interface LastStatus {
  payload: { status: string; reason: string; description?: string } | null;
  operatorName: string | null;
  operatorLoggedOut?: boolean;
}

export interface DumpOption {
  id: string;
  name: string;
  locationType: string;
  materialCategory: string[];
}

// per-excavator editable meta fields
// Raw status values that display as "READY" — activity/reason text is only
// meaningful (and only shown) while an asset is in one of these states.
const isReadyStatus = (statusRaw?: string | null): boolean => {
  const rawKey = statusRaw?.toLowerCase() ?? '';
  return rawKey === 'active' || rawKey === 'ready';
};

// same colorCode/display-name resolution the excavator status badge uses —
// shared so the Locked Trucks dropdown can show the same status per truck
export const getTruckStatusDisplay = (
  statusRaw: string | null | undefined,
  statusGroups: Map<string, { colorCode: string; reasons: string[] }>,
  theme: Theme
): { label: string; color: string } => {
  const rawKey = statusRaw?.toLowerCase() ?? '';
  const color = statusRaw
    ? statusGroups.get(rawKey)?.colorCode ?? theme.palette.action.selected
    : theme.palette.action.disabled;
  const label = rawKey === 'active' ? 'READY' : statusRaw?.toUpperCase() ?? '—';
  return { label, color };
};

interface ExcMeta {
  capacity: string;
  primaryGradeBlock: string;
  lockedTrucks: string[];
  lockedDumping: string[];
  hardlock: boolean;
}

// per-excavator manual-dispatch config — independent of the V4 ExcMeta/dirty state above
// grade/dump are stored by NAME (matching the V4 ExcMeta pattern) and resolved to
// UUIDs only at submit time — gradeId/dumpingZoneId aren't kept in state directly
export interface ManualExcMeta {
  primaryGradeBlock: string;
  // one excavator, multiple dump zones (Backend Update #6) — backend round-robins
  // locked trucks across these by list position
  dumpingZoneNames: string[];
  lockedTrucks: string[];
  needsAllocation: boolean;
  capacity: string;
  hardlock: boolean;
}

const DispatchPlanV2Inner: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { enableDarkTheme } = useAppTheme();
  const dispatch = useAppDispatch();
  const storeAssets = useAppSelector(selectAssociatedAssets);
  const customerId = useAppSelector((s) => s.persistedReducer.customer.id);
  const dispatchMonitorApi = useRef(new DispatchMonitorAPI()).current;
  const assetApi = useRef(new AssetAPI()).current;
  const locationApi = useRef(new LocationImporterAPI()).current;
  const gradeApi = useRef(new GradeImporterAPI()).current;
  const fleetPlanningApi = useRef(new FleetPlanningAPI()).current;
  const statusCodesApi = useRef(new StatusCodesAPI()).current;
  const manualDispatchApi = useRef(new ManualDispatchAPI()).current;
  // assets already "down" when page first loaded — never show bell for these
  const initialDownAtLoad = useRef<Set<string>>(new Set());

  const [assets, setAssets] = useState<AssetInfo[]>([]);
  const [lastStatuses, setLastStatuses] = useState<Record<string, LastStatus>>(
    {}
  );

  // live operator login/logout — same SSE channel as Haul Monitor, kept in
  // its own subscription rather than sharing that view's tangled handler
  useOperatorLiveStatus((device, update) => {
    setLastStatuses((prev) => ({
      ...prev,
      [device]: {
        payload: prev[device]?.payload ?? null,
        operatorName: update.operatorName,
        operatorLoggedOut: update.operatorLoggedOut,
      },
    }));
  });

  // live status (STANDBY/DOWN/ACTIVE/etc.) — same SSE channel as Haul
  // Monitor, kept in its own subscription like useOperatorLiveStatus above
  useAssetLiveStatus((device, update) => {
    setLastStatuses((prev) => ({
      ...prev,
      [device]: {
        payload: {
          status: update.status,
          reason: update.reason,
          description: update.description,
        },
        operatorName: prev[device]?.operatorName ?? null,
        operatorLoggedOut: prev[device]?.operatorLoggedOut,
      },
    }));
  });
  const [grades, setGrades] = useState<GradeRecord[]>([]);
  const [dumpOptions, setDumpOptions] = useState<DumpOption[]>([]);
  const [allLocations, setAllLocations] = useState<ImportedLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDispatching, setIsDispatching] = useState(false);
  const [sourceSessionId, setSourceSessionId] = useState<string | null>(null);
  const [statusCodes, setStatusCodes] = useState<StatusCodeRecord[]>([]);

  // local dirty meta per symbotId — merged over backend meta for display
  const [dirty, setDirty] = useState<Record<string, Partial<ExcMeta>>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [isSavingAll, setIsSavingAll] = useState(false);

  const { mode } = useDispatchMode();
  const [manualMeta, setManualMeta] = useState<Record<string, ManualExcMeta>>(
    {}
  );

  const hasDirty = Object.keys(dirty).length > 0;

  // status edit modal
  const [statusModal, setStatusModal] = useState<{
    exc: AssetInfo;
    status: string;
    reason: string;
    description: string;
  } | null>(null);
  const [statusSaving, setStatusSaving] = useState(false);

  // down confirmation
  const [downConfirmModal, setDownConfirmModal] = useState<AssetInfo | null>(
    null
  );
  const [confirmedDown, setConfirmedDown] = useState<Set<string>>(new Set());
  // symbotIds where user manually set "down" via the modal — no bell for these
  const [userSetDown, setUserSetDown] = useState<Set<string>>(new Set());

  // map modal
  const [mapLocation, setMapLocation] = useState<LocationRecord | null>(null);

  // multi-select open state
  const [truckPopOpen, setTruckPopOpen] = useState<string | null>(null);
  const [dumpPopOpen, setDumpPopOpen] = useState<string | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // ── Fetch all data ────────────────────────────────────────────────────────

  const fetchAllData = useCallback(
    async (shouldAbort: () => boolean = () => false) => {
      const [
        assetRes,
        statusRes,
        gradeRes,
        locRes,
        scRes,
        sessionRes,
        activePlanRes,
      ] = await Promise.allSettled([
        dispatchMonitorApi.getAssetInfo(),
        dispatchMonitorApi.getAssetLastStatus(),
        gradeApi.getGrades(),
        locationApi.getLocations(),
        statusCodesApi.getAll(),
        dispatchMonitorApi.getLatestDispatchSession(),
        manualDispatchApi.getActivePlan(),
      ]);

      if (shouldAbort()) return;

      const assetJson =
        assetRes.status === 'fulfilled' ? assetRes.value : { assets: [] };
      const statusJson =
        statusRes.status === 'fulfilled' ? statusRes.value : { assets: [] };
      const gradeJson = gradeRes.status === 'fulfilled' ? gradeRes.value : [];
      const locJson = locRes.status === 'fulfilled' ? locRes.value : [];
      const scJson = scRes.status === 'fulfilled' ? scRes.value : [];
      const sessionJson =
        sessionRes.status === 'fulfilled' ? sessionRes.value : null;
      const activePlanJson =
        activePlanRes.status === 'fulfilled' ? activePlanRes.value : null;

      const statusMap: Record<string, LastStatus> = {};
      (statusJson.assets ?? []).forEach((a: any) => {
        statusMap[a.symbotId] = {
          payload: a.status?.payload ?? null,
          operatorName: a.status?.operatorName ?? null,
        };
      });

      const DUMP_TYPES = new Set([
        'dumping',
        'crusher',
        'stockpile',
        'crushing',
      ]);
      const dumps: DumpOption[] = locJson
        .filter(
          (l) =>
            DUMP_TYPES.has(l.locationType?.trim()?.toLowerCase()) && l.active
        )
        .map((l) => ({
          id: l.locationId,
          name: l.name?.trim() ?? '',
          locationType: l.locationType?.trim()?.toLowerCase() ?? '',
          materialCategory: toMaterialCategoryList(l.meta?.materialCategory),
        }));

      // record which assets were already "down" on first load — no bell for these
      initialDownAtLoad.current = new Set(
        Object.entries(statusMap)
          .filter(([, s]) => s.payload?.status?.toLowerCase() === 'down')
          .map(([symbotId]) => symbotId)
      );

      setAssets(assetJson.assets ?? []);
      setLastStatuses(statusMap);
      setGrades(gradeJson);
      setDumpOptions(dumps);
      setAllLocations(locJson);
      setStatusCodes(scJson);
      setSourceSessionId(sessionJson?.session ?? null);

      // seed manual-dispatch meta up-front (in the same initial fetch) so the
      // excavator table never paints with empty rows that then fill in later.
      // Baseline comes from each excavator's own saved asset meta (Save
      // Config's persistence target) so a config survives a refresh/nav-away
      // even before any plan has ever been dispatched; an active plan's own
      // live assignments then take precedence where they exist, since those
      // reflect what's actually running rather than a last-saved intent.
      const excavatorAssets = (assetJson.assets ?? []).filter((a: AssetInfo) =>
        ['excavator', 'loader'].includes(a.assetType.trim().toLowerCase())
      );
      const nextManualMeta: Record<string, ManualExcMeta> = {};
      excavatorAssets.forEach((exc: AssetInfo) => {
        nextManualMeta[exc.symbotId] = {
          primaryGradeBlock: String(exc.meta?.primaryGradeBlock ?? ''),
          dumpingZoneNames: Array.isArray(exc.meta?.lockedDumping)
            ? (exc.meta?.lockedDumping as string[])
            : [],
          lockedTrucks: Array.isArray(exc.meta?.lockedTrucks)
            ? (exc.meta?.lockedTrucks as string[])
            : [],
          needsAllocation: false,
          capacity: String(exc.meta?.capacity ?? ''),
          hardlock: Boolean(exc.meta?.hardlock),
        };
      });
      if (activePlanJson) {
        activePlanJson.excavators.forEach((excConfig) => {
          const exc = excavatorAssets.find(
            (e: AssetInfo) => e.bumperNumber === excConfig.excavatorBumper
          );
          if (!exc) return;
          const grade = gradeJson.find(
            (g: GradeRecord) => g.id === excConfig.gradeId
          );
          const lockedTrucks = activePlanJson.assignments
            .filter((a) => a.excavatorBumper === excConfig.excavatorBumper)
            .map((a) => a.truckBumper);
          nextManualMeta[exc.symbotId] = {
            primaryGradeBlock: grade?.name ?? '',
            dumpingZoneNames: excConfig.dumpingNodes ?? [],
            lockedTrucks,
            needsAllocation: excConfig.needsAllocation,
            capacity:
              excConfig.capacity != null
                ? String(excConfig.capacity)
                : String(exc.meta?.capacity ?? ''),
            hardlock: Boolean(exc.meta?.hardlock),
          };
        });
      }
      setManualMeta(nextManualMeta);
    },
    [
      dispatchMonitorApi,
      gradeApi,
      locationApi,
      statusCodesApi,
      manualDispatchApi,
    ]
  );

  useEffect(() => {
    if (!customerId) return;
    let cancelled = false;
    fetchAllData(() => cancelled).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [customerId, fetchAllData]);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await fetchAllData();
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchAllData]);

  // warn on reload / tab-close
  useEffect(() => {
    if (!hasDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => {
      window.removeEventListener('beforeunload', handler);
    };
  }, [hasDirty]);

  // warn on browser back button (BrowserRouter doesn't support useBlocker)
  useEffect(() => {
    if (!hasDirty) return;
    window.history.pushState(null, '', window.location.href);
    const handler = () => {
      if (!window.confirm(t('dpv2_unsaved_body'))) {
        window.history.pushState(null, '', window.location.href);
      }
    };
    window.addEventListener('popstate', handler);
    return () => {
      window.removeEventListener('popstate', handler);
    };
  }, [hasDirty, t]);

  // warn on navbar / any internal link click
  useEffect(() => {
    if (!hasDirty) return;
    const handler = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement).closest('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href') ?? '';
      if (
        !href ||
        href.startsWith('http') ||
        href.startsWith('mailto') ||
        href.startsWith('#')
      )
        return;
      if (!window.confirm(t('dpv2_unsaved_body'))) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('click', handler, true);
    return () => {
      document.removeEventListener('click', handler, true);
    };
  }, [hasDirty, t]);

  // track whether table has hidden columns to the right
  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const check = () => {
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
    };
    check();
    el.addEventListener('scroll', check);
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', check);
      ro.disconnect();
    };
  }, [loading]);

  // ── Status groups from API ────────────────────────────────────────────────

  const statusGroups = useMemo(() => {
    const map = new Map<string, { colorCode: string; reasons: string[] }>();
    statusCodes.forEach((sc) => {
      const key = sc.status.trim().toLowerCase();
      if (!map.has(key)) {
        map.set(key, { colorCode: sc.colorCode, reasons: [] });
      }
      const group = map.get(key);
      const cat = sc.reasonCategory?.trim();
      if (group && cat && !group.reasons.includes(cat)) {
        group.reasons.push(cat);
      }
    });
    return map;
  }, [statusCodes]);

  // status+reasonCategory → every configured description for that pair (a
  // reason category can have several status codes, each with its own
  // description, e.g. "Fuel" → "Fuel alpha" / "Traveling To Fuelshop" /
  // "Fuel Ran Out") — feeds the Description select in the status modal.
  const descriptionsByStatusReason = useMemo(() => {
    const map = new Map<string, string[]>();
    statusCodes.forEach((sc) => {
      const statusKey = sc.status?.trim().toLowerCase();
      const reasonKey = sc.reasonCategory?.trim().toLowerCase();
      if (!statusKey || !reasonKey || !sc.description) return;
      const key = `${statusKey}__${reasonKey}`;
      const list = map.get(key);
      if (list) {
        if (!list.includes(sc.description)) list.push(sc.description);
      } else {
        map.set(key, [sc.description]);
      }
    });
    return map;
  }, [statusCodes]);

  const getDescriptionOptions = useCallback(
    (status: string | null | undefined, reason: string | null | undefined) =>
      descriptionsByStatusReason.get(
        `${status?.trim().toLowerCase() ?? ''}__${
          reason?.trim().toLowerCase() ?? ''
        }`
      ) ?? [],
    [descriptionsByStatusReason]
  );

  // Description is the specific, dispatcher-picked note for the truck's
  // current reason category — prefer it, but fall back to the raw reason
  // category for statuses set before this field existed.
  const resolveReasonDisplay = useCallback(
    (
      _status: string | null | undefined,
      reason: string | null | undefined,
      description?: string | null
    ): string | null => {
      const trimmedDescription = description?.trim();
      if (trimmedDescription) return trimmedDescription;
      return reason ?? null;
    },
    []
  );

  // ── Derived ───────────────────────────────────────────────────────────────

  const excavatorAssetTypes = ['excavator', 'loader'];

  // assets with no linked device can't report status or position — exclude
  // them everywhere rather than showing an uncontrollable "No Status" row
  const excavators = useMemo(
    () =>
      assets.filter(
        (a) =>
          excavatorAssetTypes.includes(a.assetType.trim().toLowerCase()) &&
          a.symbotId
      ),
    [assets]
  );

  const haulTrucks = useMemo(
    () =>
      assets.filter(
        (a) => a.assetType.trim().toLowerCase() === 'haul_truck' && a.symbotId
      ),
    [assets]
  );

  // grade blocks per loading location name
  const gradesByLocation = useMemo(() => {
    const map: Record<string, GradeRecord[]> = {};
    grades.forEach((g) => {
      if (!g.active) return;
      const key = g.locationName?.trim().toLowerCase() ?? '';
      if (!key) return;
      map[key] = [...(map[key] ?? []), g];
    });
    return map;
  }, [grades]);

  // resolve effective meta (backend + local dirty layer)
  const effectiveMeta = useCallback(
    (exc: AssetInfo): ExcMeta => {
      const m = exc.meta ?? {};
      const d = dirty[exc.symbotId] ?? {};
      return {
        capacity:
          d.capacity !== undefined ? d.capacity : String(m.capacity ?? ''),
        primaryGradeBlock:
          d.primaryGradeBlock !== undefined
            ? d.primaryGradeBlock
            : String(m.primaryGradeBlock ?? m.gradeBlock ?? ''),
        lockedTrucks:
          d.lockedTrucks !== undefined
            ? d.lockedTrucks
            : Array.isArray(m.lockedTrucks)
            ? (m.lockedTrucks as string[])
            : [],
        lockedDumping:
          d.lockedDumping !== undefined
            ? d.lockedDumping
            : Array.isArray(m.lockedDumping)
            ? (m.lockedDumping as string[])
            : [],
        hardlock: d.hardlock !== undefined ? d.hardlock : Boolean(m.hardlock),
      };
    },
    [dirty]
  );

  // Mirrors the same formulas the row cells below compute inline — kept as
  // a parallel set of getters (rather than refactoring the row render to
  // consume them) so sorting can't accidentally change what's displayed.
  const getRowNearbyBlocks = (exc: AssetInfo): GradeRecord[] => {
    const locationName = (exc.nearestLoading?.name ?? '').trim();
    return gradesByLocation[locationName.toLowerCase()] ?? [];
  };

  const excavatorSortGetters: SortValueGetters<AssetInfo> = {
    excavator: (exc) => exc.bumperNumber,
    capacity: (exc) => {
      const num = parseFloat(effectiveMeta(exc).capacity);
      return isFinite(num) ? num : null;
    },
    status: (exc) => lastStatuses[exc.symbotId]?.payload?.status ?? null,
    location: (exc) => {
      const name = (exc.nearestLoading?.name ?? '').trim();
      if (!name) return null;
      return name;
    },
    nearbyBlocks: (exc) => getRowNearbyBlocks(exc).length,
    primaryBlock: (exc) => {
      const value = effectiveMeta(exc).primaryGradeBlock;
      if (!value) return null;
      return value;
    },
    materialGrade: (exc) => {
      const meta = effectiveMeta(exc);
      const primaryBlock = getRowNearbyBlocks(exc).find(
        (g) =>
          g.name.trim().toLowerCase() ===
          meta.primaryGradeBlock.trim().toLowerCase()
      );
      return primaryBlock?.materialCategory ?? null;
    },
    lockedTrucks: (exc) => effectiveMeta(exc).lockedTrucks.length,
    lockedDumps: (exc) => effectiveMeta(exc).lockedDumping.length,
    hardlock: (exc) => (effectiveMeta(exc).hardlock ? 1 : 0),
  };

  const {
    sortedRows: sortedExcavators,
    sortKey: excSortKey,
    direction: excDirection,
    toggleSort: toggleExcSort,
  } = useSortableRows(excavators, excavatorSortGetters);

  // bumperNumber -> symbotId of the excavator that currently has it locked
  const truckLockOwner = useMemo(() => {
    const map: Record<string, string> = {};
    excavators.forEach((exc) => {
      effectiveMeta(exc).lockedTrucks.forEach((bumperNumber) => {
        map[bumperNumber] = exc.symbotId;
      });
    });
    return map;
  }, [excavators, effectiveMeta]);

  // ── Save patch ────────────────────────────────────────────────────────────

  const patchMeta = useCallback(
    async (exc: AssetInfo, patch: Partial<ExcMeta>): Promise<boolean> => {
      const storeAsset = storeAssets.find(
        (a) => a.device?.deviceId === exc.symbotId
      );
      const assetId = storeAsset?.assetId;
      if (!assetId || !customerId) return false;

      setSaving((prev) => ({ ...prev, [exc.symbotId]: true }));
      try {
        const existing = exc.meta ?? {};
        const updated = { ...existing, ...patch };
        await assetApi.updateMeta(assetId, updated);
        // merge into asset meta so next dirty compute is correct
        exc.meta = updated as Record<string, unknown>;
        setDirty((prev) => {
          const { [exc.symbotId]: _removed, ...rest } = prev;
          return rest;
        });
        return true;
      } catch (_) {
        // keep dirty so user can retry
        return false;
      } finally {
        setSaving((prev) => ({ ...prev, [exc.symbotId]: false }));
      }
    },
    [storeAssets, customerId, assetApi]
  );

  const setField = useCallback(
    (exc: AssetInfo, field: keyof ExcMeta, value: ExcMeta[keyof ExcMeta]) => {
      setDirty((prev) => ({
        ...prev,
        [exc.symbotId]: { ...prev[exc.symbotId], [field]: value },
      }));
    },
    []
  );

  const saveAll = useCallback(async () => {
    if (!hasDirty) return;
    setIsSavingAll(true);
    try {
      await Promise.all(
        excavators
          .filter((exc) => dirty[exc.symbotId] !== undefined)
          .map(async (exc) => {
            await patchMeta(exc, dirty[exc.symbotId]);
          })
      );
    } finally {
      setIsSavingAll(false);
    }
  }, [hasDirty, excavators, dirty, patchMeta]);

  const updateStatus = useCallback(async () => {
    if (!statusModal || !customerId) return;
    const storeAsset = storeAssets.find(
      (a) => a.device?.deviceId === statusModal.exc.symbotId
    );
    const assetId = storeAsset?.assetId;
    if (!assetId) return;

    if (mode === 'manual' && statusModal.status.toLowerCase() === 'active') {
      const excMeta = manualMeta[statusModal.exc.symbotId];
      if (
        !excMeta?.primaryGradeBlock ||
        !excMeta?.dumpingZoneNames ||
        excMeta.dumpingZoneNames.length === 0
      ) {
        dispatch(
          showNotification({
            message: t('dpv2_manual_ready_requires_config'),
            type: 'error',
          })
        );
        return;
      }
    }
    setStatusSaving(true);
    try {
      await dispatchMonitorApi.updateAssetStatus(
        assetId,
        statusModal.status,
        statusModal.reason?.trim() || undefined,
        statusModal.description?.trim() || undefined
      );
      setLastStatuses((prev) => ({
        ...prev,
        [statusModal.exc.symbotId]: {
          payload: {
            status: statusModal.status,
            reason: statusModal.reason.trim(),
            description: statusModal.description?.trim() ?? '',
          },
          operatorName: prev[statusModal.exc.symbotId]?.operatorName ?? null,
        },
      }));
      // track whether user intentionally set this asset to "down"
      const sid = statusModal.exc.symbotId;
      if (statusModal.status.toLowerCase() === 'down') {
        setUserSetDown((prev) => {
          const next = new Set(prev);
          next.add(sid);
          return next;
        });
      } else {
        setUserSetDown((prev) => {
          const next = new Set(prev);
          next.delete(sid);
          return next;
        });
      }
      setStatusModal(null);
    } catch (_) {
      // keep modal open so user can retry
    } finally {
      setStatusSaving(false);
    }
  }, [
    statusModal,
    customerId,
    storeAssets,
    dispatchMonitorApi,
    mode,
    manualMeta,
    dispatch,
    t,
  ]);

  // shared by both the auto (V4) table and the manual panel's Location column
  const openMapForExcavator = useCallback(
    (exc: AssetInfo) => {
      const loc = exc.nearestLoading;
      if (!loc) return;
      const full = allLocations.find((l) => l.locationId === loc.locationId);
      setMapLocation({
        id: loc.locationId,
        name: loc.name,
        locationType: full?.locationType ?? 'loading',
        easting: loc.easting,
        northing: loc.northing,
        centroidLat: full?.centroidLat ?? undefined,
        centroidLon: full?.centroidLon ?? undefined,
        elevation: 0,
        active: true,
      });
    },
    [allLocations]
  );

  // ── Dispatch Plan ────────────────────────────────────────────────────────

  const handleDispatchPlan = useCallback(async () => {
    if (!customerId) return;
    setIsDispatching(true);
    try {
      // getGrades() returns raw API shape where UUID is under `gradeId`, not `id`
      const resolveGradeId = (g: GradeRecord): string =>
        ((g as unknown as Record<string, unknown>).gradeId as string) ?? g.id;

      const gradeByName = new Map<string, GradeRecord>();
      grades.forEach((g) => {
        gradeByName.set(g.name.trim().toLowerCase(), g);
      });

      // lockedDumping stores names — map to locationId UUIDs
      const dumpIdByName = new Map<string, string>();
      dumpOptions.forEach((d) => {
        dumpIdByName.set(d.name.trim().toLowerCase(), d.id);
      });

      const excavatorPayload = excavators.flatMap((exc) => {
        const meta = effectiveMeta(exc);

        // Skip down assets
        const excStatus =
          lastStatuses[exc.symbotId]?.payload?.status?.toLowerCase();
        if (excStatus === 'down') return [];

        // Skip if no primaryGradeBlock set
        if (!meta.primaryGradeBlock.trim()) return [];

        const gradeRecord = gradeByName.get(
          meta.primaryGradeBlock.trim().toLowerCase()
        );
        if (!gradeRecord) return [];

        const lockedDumps = meta.lockedDumping.flatMap((name) => {
          const id = dumpIdByName.get(name.trim().toLowerCase());
          return id ? [id] : [];
        });

        // Same grade blocks shown in the "Nearby Grade Blocks" column —
        // every grade block at this excavator's current loading location
        const locationName = (exc.nearestLoading?.name ?? '').trim();
        const nearbyBlocks = gradesByLocation[locationName.toLowerCase()] ?? [];
        const nearbyGradeBlock = nearbyBlocks.map((g) => resolveGradeId(g));

        return [
          {
            bumperNumber: exc.bumperNumber,
            gradeId: resolveGradeId(gradeRecord),
            capacity: Number(meta.capacity) || 0,
            lockedTrucks: meta.lockedTrucks,
            lockedDumps,
            hardLock: meta.hardlock,
            nearbyGradeBlock,
          },
        ];
      });

      // send every truck so the solver can decide assignment across all
      // excavators — a truck with no live GPS fix (nearestLoading/nearestDumping
      // both null) still goes through, just without a startLocationId
      const truckPayload = haulTrucks.map((truck) => {
        const nl = truck.nearestLoading;
        const nd = truck.nearestDumping;
        let startLocationId = '';
        if (nl && nd) {
          startLocationId =
            nl.distanceMeters <= nd.distanceMeters
              ? nl.locationId
              : nd.locationId;
        } else if (nl) {
          startLocationId = nl.locationId;
        } else if (nd) {
          startLocationId = nd.locationId;
        }
        return startLocationId
          ? { bumperNumber: truck.bumperNumber, startLocationId }
          : { bumperNumber: truck.bumperNumber };
      });

      await fleetPlanningApi.createDispatchPlanV4(customerId, {
        overtimeTolerance: 30,
        excavators: excavatorPayload,
        trucks: truckPayload,
        saveAndDispatch: true,
        overrideDispatchPlan: false,
        ...(sourceSessionId ? { source_session_id: sourceSessionId } : {}),
      });

      dispatch(
        showNotification({
          message: t('dpv2_dispatch_success'),
          type: 'success',
        })
      );

      try {
        const latestSession =
          await dispatchMonitorApi.getLatestDispatchSession();
        setSourceSessionId(latestSession?.session ?? null);
      } catch (sessionErr) {
        // session refresh is best-effort; button label just won't flip to "Update Plan" yet
        console.error(sessionErr);
      }
    } catch (err: unknown) {
      const apiMessage =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? null;
      dispatch(
        showNotification({
          message: apiMessage ?? t('dpv2_dispatch_failed'),
          type: 'error',
        })
      );
    } finally {
      setIsDispatching(false);
    }
  }, [
    customerId,
    grades,
    gradesByLocation,
    dumpOptions,
    excavators,
    haulTrucks,
    effectiveMeta,
    fleetPlanningApi,
    dispatchMonitorApi,
    sourceSessionId,
    dispatch,
    t,
  ]);

  // ── Colours ───────────────────────────────────────────────────────────────

  const bg = enableDarkTheme
    ? theme.palette.background.default
    : theme.palette.grey[100];

  const panelBg = enableDarkTheme
    ? alpha(theme.palette.common.white, 0.04)
    : theme.palette.background.paper;

  const borderColor = enableDarkTheme
    ? alpha(theme.palette.common.white, 0.08)
    : theme.palette.divider;

  const textPrimary = theme.palette.text.primary;
  const textMuted = theme.palette.text.secondary;
  // sticky column must use a solid opaque color — panelBg is semi-transparent in dark mode
  const stickyBg = theme.palette.background.paper;

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress size={36} />
      </Box>
    );
  }

  return (
    <Box
      ref={popRef}
      sx={{
        p: 3,
        pb: 6,
        backgroundColor: bg,
        height: 'calc(100vh - 160px)',
        overflowY: 'auto',
      }}
    >
      {mode === 'manual' && (
        <ManualExcavatorPanel
          excavators={excavators}
          haulTrucks={haulTrucks}
          lastStatuses={lastStatuses}
          grades={grades}
          gradesByLocation={gradesByLocation}
          dumpOptions={dumpOptions}
          statusGroups={statusGroups}
          resolveReasonDisplay={resolveReasonDisplay}
          manualMeta={manualMeta}
          setManualMeta={setManualMeta}
          onOpenStatusModal={(exc) => {
            setStatusModal({ exc, status: '', reason: '', description: '' });
          }}
          onOpenMap={openMapForExcavator}
          onToggleHardLock={async (exc, checked) => {
            // notification is shown once, in bulk, by the Save Config button —
            // just signal failure to its Promise.allSettled via a rejection
            const ok = await patchMeta(exc, { hardlock: checked });
            if (!ok) throw new Error('Failed to update hard lock');
          }}
          onCapacityChange={async (exc, capacity) => {
            const ok = await patchMeta(exc, { capacity });
            if (!ok) throw new Error('Failed to update capacity');
          }}
          onSaveManualConfig={async (exc, patch) => {
            // reuses the same asset-meta endpoint AUTO mode's Save already
            // persists lockedTrucks/primaryGradeBlock/lockedDumping through —
            // this is what makes Save Config's success toast honest instead
            // of silently no-oping for these fields (only Dispatch Plan used
            // to actually send them anywhere)
            const metaPatch: Partial<ExcMeta> = {};
            if (patch.primaryGradeBlock !== undefined) {
              metaPatch.primaryGradeBlock = patch.primaryGradeBlock;
            }
            if (patch.dumpingZoneNames !== undefined) {
              metaPatch.lockedDumping = patch.dumpingZoneNames;
            }
            if (patch.lockedTrucks !== undefined) {
              metaPatch.lockedTrucks = patch.lockedTrucks;
            }
            const ok = await patchMeta(exc, metaPatch);
            if (!ok) throw new Error('Failed to save excavator config');
          }}
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
        />
      )}

      {mode === 'auto' && (
        <>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ color: textPrimary }}
            >
              {t('dpv2_excavator_inputs')}
            </Typography>
            <Box
              sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}
            >
              <Tooltip title={t('dpv2_btn_refresh')}>
                <span>
                  <IconButton
                    onClick={() => {
                      void handleRefresh();
                    }}
                    disabled={isRefreshing || isDispatching}
                    size="small"
                    sx={{
                      border: '1px solid',
                      borderColor: (th) => alpha(th.palette.divider, 0.6),
                      borderRadius: '8px',
                      p: 0.8,
                    }}
                  >
                    <RefreshIcon
                      fontSize="small"
                      sx={{
                        transition: 'transform 0.5s',
                        transform: isRefreshing ? 'rotate(360deg)' : 'none',
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                variant="outlined"
                size="small"
                startIcon={
                  isDispatching ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <SendIcon fontSize="small" />
                  )
                }
                disabled={isDispatching || hasDirty}
                onClick={() => {
                  void handleDispatchPlan();
                }}
                sx={{ fontWeight: 700, textTransform: 'none' }}
              >
                {isDispatching
                  ? t('dpv2_dispatching')
                  : sourceSessionId
                  ? t('dpv2_update_plan')
                  : t('dpv2_dispatch_plan')}
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={
                  isSavingAll ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <SaveIcon fontSize="small" />
                  )
                }
                disabled={!hasDirty || isSavingAll}
                onClick={() => {
                  void saveAll();
                }}
                sx={{ fontWeight: 700, textTransform: 'none' }}
              >
                {t('dpv2_save_config')}
              </Button>
            </Box>
          </Box>

          {/* Table card */}
          <Box
            sx={{
              bgcolor: panelBg,
              border: `1px solid ${borderColor}`,
              borderRadius: 2,
              position: 'relative',
            }}
          >
            {canScrollRight && (
              <>
                {/* non-interactive gradient fade */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: 72,
                    pointerEvents: 'none',
                    zIndex: 2,
                    borderRadius: '0 8px 8px 0',
                    background: `linear-gradient(to right, transparent, ${panelBg} 80%)`,
                  }}
                />
                {/* interactive badge with tooltip */}
                <Tooltip title={t('dpv2_scroll_hint')} placement="left" arrow>
                  <Box
                    onClick={() => {
                      if (tableScrollRef.current) {
                        tableScrollRef.current.scrollTo({
                          left: tableScrollRef.current.scrollWidth,
                          behavior: 'smooth',
                        });
                      }
                    }}
                    sx={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      zIndex: 3,
                      px: 0.75,
                      py: 0.3,
                      borderRadius: '999px',
                      backgroundColor: alpha(theme.palette.primary.main, 0.18),
                      border: `1px solid ${alpha(
                        theme.palette.primary.main,
                        0.35
                      )}`,
                      fontSize: 11,
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      userSelect: 'none',
                      lineHeight: 1.4,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.28
                        ),
                      },
                    }}
                  >
                    ›››
                  </Box>
                </Tooltip>
              </>
            )}
            <Box ref={tableScrollRef} sx={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 12.5,
                }}
              >
                <thead>
                  <tr>
                    <SortableTh
                      label={t('dpv2_col_excavator')}
                      sortKey="excavator"
                      activeSortKey={excSortKey}
                      direction={excDirection}
                      onSort={toggleExcSort}
                      style={{
                        textAlign: 'left',
                        padding: '10px 12px',
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        color: textMuted,
                        borderBottom: `1px solid ${borderColor}`,
                        whiteSpace: 'nowrap',
                        backgroundColor: stickyBg,
                        position: 'sticky',
                        left: 0,
                        zIndex: 4,
                        boxShadow: '2px 0 6px -2px rgba(0,0,0,0.15)',
                      }}
                    />
                    {[
                      { key: 'capacity', label: t('dpv2_col_capacity') },
                      { key: 'status', label: t('dpv2_col_status') },
                      { key: 'location', label: t('dpv2_col_location') },
                      {
                        key: 'nearbyBlocks',
                        label: t('dpv2_col_nearby_blocks'),
                      },
                      {
                        key: 'primaryBlock',
                        label: t('dpv2_col_primary_block'),
                      },
                      {
                        key: 'materialGrade',
                        label: t('dpv2_col_material_grade'),
                      },
                      {
                        key: 'lockedTrucks',
                        label: t('dpv2_col_locked_trucks'),
                      },
                      { key: 'lockedDumps', label: t('dpv2_col_locked_dumps') },
                      { key: 'hardlock', label: t('dpv2_col_hardlock') },
                    ].map((col) => (
                      <SortableTh
                        key={col.key}
                        label={col.label}
                        sortKey={col.key}
                        activeSortKey={excSortKey}
                        direction={excDirection}
                        onSort={toggleExcSort}
                        style={{
                          textAlign: 'left',
                          padding: '10px 12px',
                          fontSize: 10,
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          color: textMuted,
                          borderBottom: `1px solid ${borderColor}`,
                          whiteSpace: 'nowrap',
                          backgroundColor: panelBg,
                        }}
                      />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {excavators.length === 0 && (
                    <tr>
                      <td
                        colSpan={10}
                        style={{
                          padding: '24px',
                          textAlign: 'center',
                          color: textMuted,
                        }}
                      >
                        {t('dpv2_no_excavators')}
                      </td>
                    </tr>
                  )}
                  {sortedExcavators.map((exc) => {
                    const meta = effectiveMeta(exc);
                    const isSaving = Boolean(saving[exc.symbotId]);
                    const isHardLocked = meta.hardlock;
                    const st = lastStatuses[exc.symbotId];
                    const statusRaw = st?.payload?.status ?? null;
                    const locationName =
                      (exc.nearestLoading?.name ?? '').trim() || '—';
                    const nearbyBlocks =
                      gradesByLocation[locationName.trim().toLowerCase()] ?? [];
                    const primaryBlock = nearbyBlocks.find(
                      (g) =>
                        g.name.trim().toLowerCase() ===
                        meta.primaryGradeBlock.trim().toLowerCase()
                    );
                    const gradeMaterialCategory = primaryBlock?.materialCategory
                      ?.trim()
                      .toLowerCase();
                    const rowDumpOptions =
                      primaryBlock && gradeMaterialCategory
                        ? dumpOptions.filter(
                            (d) =>
                              d.locationType === 'stockpile' ||
                              d.materialCategory.some(
                                (c) =>
                                  c.trim().toLowerCase() ===
                                  gradeMaterialCategory
                              )
                          )
                        : dumpOptions;
                    const rowOpacity =
                      statusRaw?.toLowerCase() === 'down' ? 0.55 : 1;

                    return (
                      <tr key={exc.symbotId} style={{ opacity: rowOpacity }}>
                        {/* Excavator */}
                        <td
                          style={{
                            padding: '10px 12px',
                            borderBottom: `1px solid ${borderColor}`,
                            position: 'sticky',
                            left: 0,
                            zIndex: 3,
                            backgroundColor: stickyBg,
                            boxShadow: '2px 0 6px -2px rgba(0,0,0,0.15)',
                          }}
                        >
                          <Typography
                            fontWeight={700}
                            fontSize={13}
                            sx={{ color: textPrimary }}
                          >
                            {exc.bumperNumber}
                          </Typography>
                        </td>

                        {/* Capacity */}
                        <td
                          style={{
                            padding: '10px 12px',
                            borderBottom: `1px solid ${borderColor}`,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.75,
                            }}
                          >
                            <TextField
                              size="small"
                              value={meta.capacity}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                  setField(exc, 'capacity', val);
                                }
                              }}
                              disabled={isSaving || isHardLocked}
                              placeholder="0"
                              inputProps={{
                                inputMode: 'decimal',
                                style: {
                                  fontSize: 12,
                                  padding: '5px 8px',
                                  width: 70,
                                },
                              }}
                              sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: 1 },
                              }}
                            />
                            <Typography
                              fontSize={11}
                              sx={{ color: textMuted, whiteSpace: 'nowrap' }}
                            >
                              t/hr
                            </Typography>
                          </Box>
                        </td>

                        {/* Status */}
                        <td
                          style={{
                            padding: '10px 12px',
                            borderBottom: `1px solid ${borderColor}`,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.75,
                            }}
                          >
                            {(() => {
                              const statusDisplayNames: Record<string, string> =
                                {
                                  active: 'READY',
                                };
                              const bg = statusRaw
                                ? statusGroups.get(statusRaw.toLowerCase())
                                    ?.colorCode ?? theme.palette.action.selected
                                : 'transparent';
                              const rawKey = statusRaw?.toLowerCase() ?? '';
                              const displayName =
                                statusDisplayNames[rawKey] ??
                                statusRaw?.toUpperCase() ??
                                '';
                              // Activity/reason text is only meaningful while
                              // ready — any other status shows a dash instead.
                              const reasonText = isReadyStatus(statusRaw)
                                ? resolveReasonDisplay(
                                    statusRaw,
                                    st?.payload?.reason,
                                    st?.payload?.description
                                  )
                                : '—';
                              const label = statusRaw
                                ? [displayName, reasonText]
                                    .filter(Boolean)
                                    .join(' · ')
                                : t('dpv2_no_status');
                              return (
                                <Box
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    px: 1,
                                    py: 0.4,
                                    borderRadius: 1.5,
                                    backgroundColor: statusRaw
                                      ? bg
                                      : 'transparent',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  <Box
                                    component="span"
                                    sx={{
                                      fontSize: 11,
                                      fontWeight: 700,
                                      color: statusRaw ? '#fff' : textMuted,
                                      letterSpacing: '0.02em',
                                    }}
                                  >
                                    {label}
                                  </Box>
                                  <IconButton
                                    size="small"
                                    disabled={isSaving}
                                    onClick={() => {
                                      setStatusModal({
                                        exc,
                                        status: statusRaw ?? '',
                                        reason: st?.payload?.reason ?? '',
                                        description:
                                          st?.payload?.description ?? '',
                                      });
                                    }}
                                    sx={{
                                      p: 0.1,
                                      color: statusRaw
                                        ? 'rgba(255,255,255,0.7)'
                                        : textMuted,
                                      '&:hover': {
                                        color: statusRaw
                                          ? '#fff'
                                          : theme.palette.primary.main,
                                      },
                                    }}
                                  >
                                    <EditIcon sx={{ fontSize: 13 }} />
                                  </IconButton>
                                </Box>
                              );
                            })()}
                            {statusRaw?.toLowerCase() === 'down' &&
                              !initialDownAtLoad.current.has(exc.symbotId) &&
                              !confirmedDown.has(exc.symbotId) &&
                              !userSetDown.has(exc.symbotId) && (
                                <Tooltip title={t('dpv2_down_confirm_tooltip')}>
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setDownConfirmModal(exc);
                                    }}
                                    sx={{
                                      p: 0.25,
                                      color: '#ef4444',
                                      '@keyframes dpv2-bell': {
                                        '0%, 100%': {
                                          transform: 'scale(1)',
                                          opacity: 1,
                                        },
                                        '50%': {
                                          transform: 'scale(1.25)',
                                          opacity: 0.65,
                                        },
                                      },
                                      animation:
                                        'dpv2-bell 1.4s ease-in-out infinite',
                                    }}
                                  >
                                    <NotificationsActiveOutlinedIcon
                                      sx={{ fontSize: 16 }}
                                    />
                                  </IconButton>
                                </Tooltip>
                              )}
                          </Box>
                        </td>

                        {/* Location */}
                        <td
                          style={{
                            padding: '10px 12px',
                            borderBottom: `1px solid ${borderColor}`,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              fontSize={11.5}
                              sx={{ color: textMuted }}
                            >
                              📍 {locationName}
                            </Typography>
                            {exc.nearestLoading && (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  openMapForExcavator(exc);
                                }}
                                sx={{
                                  p: 0.25,
                                  color: textMuted,
                                  '&:hover': {
                                    color: theme.palette.primary.main,
                                  },
                                }}
                              >
                                <MapIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            )}
                          </Box>
                        </td>

                        {/* Nearby Grade Blocks */}
                        <td
                          style={{
                            padding: '10px 12px',
                            borderBottom: `1px solid ${borderColor}`,
                          }}
                        >
                          <Box
                            sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}
                          >
                            {nearbyBlocks.length === 0 ? (
                              <Typography
                                fontSize={11}
                                sx={{ color: textMuted }}
                              >
                                —
                              </Typography>
                            ) : (
                              nearbyBlocks.map((g) => (
                                <Chip
                                  key={g.id}
                                  label={g.name}
                                  size="small"
                                  sx={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    height: 20,
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.12
                                    ),
                                    color: theme.palette.primary.main,
                                  }}
                                />
                              ))
                            )}
                          </Box>
                        </td>

                        {/* Primary Grade Block */}
                        <td
                          style={{
                            padding: '10px 12px',
                            borderBottom: `1px solid ${borderColor}`,
                          }}
                        >
                          {nearbyBlocks.length === 0 ? (
                            <Typography fontSize={11} sx={{ color: textMuted }}>
                              —
                            </Typography>
                          ) : (
                            <Autocomplete
                              size="small"
                              options={[...nearbyBlocks].sort((a, b) =>
                                a.name.localeCompare(b.name)
                              )}
                              getOptionLabel={(g) => g.name}
                              isOptionEqualToValue={(g, v) => g.id === v.id}
                              value={
                                nearbyBlocks.find(
                                  (g) => g.name === meta.primaryGradeBlock
                                ) ?? null
                              }
                              onChange={(_, g) => {
                                setField(
                                  exc,
                                  'primaryGradeBlock',
                                  g?.name ?? ''
                                );
                                setField(exc, 'lockedDumping', []);
                              }}
                              disabled={isSaving || isHardLocked}
                              sx={{
                                minWidth: 150,
                                maxWidth: 220,
                                '& .MuiAutocomplete-input': {
                                  fontSize: 12,
                                  textOverflow: 'ellipsis',
                                },
                              }}
                              componentsProps={{
                                popper: {
                                  style: {
                                    width: 'max-content',
                                    minWidth: 220,
                                  },
                                  placement: 'bottom-start',
                                },
                                paper: {
                                  sx: {
                                    whiteSpace: 'nowrap',
                                    '& .MuiAutocomplete-option': {
                                      fontSize: 12,
                                      minHeight: 'auto',
                                    },
                                  },
                                },
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder={t('dpv2_select_grade_block')}
                                />
                              )}
                            />
                          )}
                        </td>

                        {/* Material Grade */}
                        <td
                          style={{
                            padding: '10px 12px',
                            borderBottom: `1px solid ${borderColor}`,
                          }}
                        >
                          {primaryBlock?.materialCategory ? (
                            <Typography
                              fontSize={12}
                              fontWeight={700}
                              sx={{ color: textPrimary }}
                            >
                              {primaryBlock.materialCategory}
                            </Typography>
                          ) : (
                            <Typography fontSize={11} sx={{ color: textMuted }}>
                              —
                            </Typography>
                          )}
                        </td>

                        {/* Locked Trucks */}
                        <td
                          style={{
                            padding: '10px 12px',
                            borderBottom: `1px solid ${borderColor}`,
                            minWidth: 180,
                          }}
                        >
                          <MultiSelectCell
                            popId={`trk-${exc.symbotId}`}
                            open={truckPopOpen === `trk-${exc.symbotId}`}
                            onToggle={() => {
                              setTruckPopOpen((p) =>
                                p === `trk-${exc.symbotId}`
                                  ? null
                                  : `trk-${exc.symbotId}`
                              );
                              setDumpPopOpen(null);
                            }}
                            onClose={() => {
                              setTruckPopOpen(null);
                            }}
                            selected={meta.lockedTrucks}
                            options={haulTrucks
                              .filter((t) => {
                                const owner = truckLockOwner[t.bumperNumber];
                                return !owner || owner === exc.symbotId;
                              })
                              .map((t) => {
                                const truckStatus = getTruckStatusDisplay(
                                  lastStatuses[t.symbotId]?.payload?.status,
                                  statusGroups,
                                  theme
                                );
                                return {
                                  id: t.bumperNumber,
                                  label: t.bumperNumber,
                                  sub: '',
                                  statusLabel: truckStatus.label,
                                  statusColor: truckStatus.color,
                                };
                              })}
                            onToggleItem={(id) => {
                              const next = meta.lockedTrucks.includes(id)
                                ? meta.lockedTrucks.filter((x) => x !== id)
                                : [...meta.lockedTrucks, id];
                              setField(exc, 'lockedTrucks', next);
                            }}
                            placeholder={t('dpv2_select_trucks')}
                            enableDarkTheme={enableDarkTheme}
                            borderColor={borderColor}
                            textMuted={textMuted}
                            textPrimary={textPrimary}
                            theme={theme}
                            disabled={isHardLocked || isSaving}
                          />
                        </td>

                        {/* Locked Dumps */}
                        <td
                          style={{
                            padding: '10px 12px',
                            borderBottom: `1px solid ${borderColor}`,
                            minWidth: 180,
                          }}
                        >
                          <MultiSelectCell
                            popId={`dmp-${exc.symbotId}`}
                            open={dumpPopOpen === `dmp-${exc.symbotId}`}
                            onToggle={() => {
                              setDumpPopOpen((p) =>
                                p === `dmp-${exc.symbotId}`
                                  ? null
                                  : `dmp-${exc.symbotId}`
                              );
                              setTruckPopOpen(null);
                            }}
                            onClose={() => {
                              setDumpPopOpen(null);
                            }}
                            selected={meta.lockedDumping}
                            options={rowDumpOptions.map((d) => ({
                              id: d.name,
                              label: d.name,
                              sub: '',
                            }))}
                            onToggleItem={(id) => {
                              const next = meta.lockedDumping.includes(id)
                                ? meta.lockedDumping.filter((x) => x !== id)
                                : [...meta.lockedDumping, id];
                              setField(exc, 'lockedDumping', next);
                            }}
                            placeholder={t('dpv2_select_dumps')}
                            enableDarkTheme={enableDarkTheme}
                            borderColor={borderColor}
                            textMuted={textMuted}
                            textPrimary={textPrimary}
                            theme={theme}
                            disabled={isHardLocked || isSaving}
                          />
                        </td>

                        {/* Hard Lock */}
                        <td
                          style={{
                            padding: '10px 12px',
                            borderBottom: `1px solid ${borderColor}`,
                            textAlign: 'center',
                          }}
                        >
                          <Checkbox
                            checked={meta.hardlock}
                            disabled={isSaving}
                            onChange={(_, checked) => {
                              setField(exc, 'hardlock', checked);
                            }}
                            size="small"
                            sx={{ p: 0.5 }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Box>
          </Box>
        </>
      )}

      {/* ── Location Map Modal ── */}
      <LocationMapModal
        open={Boolean(mapLocation)}
        onClose={() => {
          setMapLocation(null);
        }}
        location={mapLocation}
      />

      {/* ── Status Edit Modal ── */}
      <Dialog
        open={Boolean(statusModal)}
        onClose={() => {
          setStatusModal(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
          {t('dpv2_edit_status_title')}
          {statusModal ? ` — ${statusModal.exc.bumperNumber}` : ''}
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
            pt: '16px !important',
          }}
        >
          <Box>
            <Typography
              fontSize={10}
              fontWeight={700}
              letterSpacing="0.08em"
              sx={{ color: textMuted, textTransform: 'uppercase', mb: 1 }}
            >
              {t('dpv2_edit_status_label')}
            </Typography>
            <Box
              sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}
            >
              {Array.from(statusGroups.entries()).map(
                ([key, { colorCode }]) => {
                  const isSelected = statusModal?.status?.toLowerCase() === key;
                  return (
                    <Box
                      key={key}
                      onClick={() => {
                        if (statusModal) {
                          const firstReason =
                            statusGroups.get(key)?.reasons[0] ?? '';
                          setStatusModal({
                            ...statusModal,
                            status: key,
                            reason: firstReason,
                            description:
                              getDescriptionOptions(key, firstReason)[0] ?? '',
                          });
                        }
                      }}
                      sx={{
                        backgroundColor: colorCode,
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: 14,
                        textAlign: 'center',
                        py: 1.5,
                        borderRadius: 2,
                        cursor: 'pointer',
                        outline: isSelected
                          ? '2.5px solid #fff'
                          : '2.5px solid transparent',
                        outlineOffset: '2px',
                        opacity: isSelected ? 1 : 0.72,
                        transition: 'opacity 0.15s, outline 0.15s',
                        userSelect: 'none',
                        textTransform: 'capitalize',
                        '&:hover': { opacity: 1 },
                      }}
                    >
                      {key}
                    </Box>
                  );
                }
              )}
            </Box>
          </Box>
          {statusModal?.status &&
            (
              statusGroups.get(statusModal.status?.toLowerCase() ?? '')
                ?.reasons ?? []
            ).length > 0 && (
              <Box>
                <Typography
                  fontSize={10}
                  fontWeight={700}
                  letterSpacing="0.08em"
                  sx={{ color: textMuted, textTransform: 'uppercase', mb: 1 }}
                >
                  {t('dpv2_edit_reason_label')}
                </Typography>
                <Select
                  fullWidth
                  size="small"
                  value={statusModal.reason}
                  onChange={(e) => {
                    const nextReason = e.target.value;
                    setStatusModal({
                      ...statusModal,
                      reason: nextReason,
                      description:
                        getDescriptionOptions(
                          statusModal.status,
                          nextReason
                        )[0] ?? '',
                    });
                  }}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em style={{ color: 'inherit', opacity: 0.5 }}>
                      {t('dpv2_reason_placeholder')}
                    </em>
                  </MenuItem>
                  {(
                    statusGroups.get(statusModal.status?.toLowerCase() ?? '')
                      ?.reasons ?? []
                  ).map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            )}

          {statusModal?.status && statusModal.reason && (
            <Box>
              <Typography
                fontSize={10}
                fontWeight={700}
                letterSpacing="0.08em"
                sx={{ color: textMuted, textTransform: 'uppercase', mb: 1 }}
              >
                {t('hm_edit_description_label')}
              </Typography>
              <Autocomplete
                freeSolo
                size="small"
                options={getDescriptionOptions(
                  statusModal.status,
                  statusModal.reason
                )}
                value={statusModal.description}
                onChange={(_e, value) => {
                  setStatusModal({
                    ...statusModal,
                    description: value ?? '',
                  });
                }}
                onInputChange={(_e, value) => {
                  setStatusModal({ ...statusModal, description: value });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t('hm_description_placeholder')}
                  />
                )}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setStatusModal(null);
            }}
            disabled={statusSaving}
            sx={{ textTransform: 'none' }}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={statusSaving || !statusModal?.status}
            onClick={() => {
              void updateStatus();
            }}
            startIcon={
              statusSaving ? (
                <CircularProgress size={13} color="inherit" />
              ) : null
            }
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {t('dpv2_edit_status_save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Down Confirmation Modal ── */}
      <Dialog
        open={Boolean(downConfirmModal)}
        onClose={() => {
          setDownConfirmModal(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 15 }}>
          {t('dpv2_down_confirm_title')}
        </DialogTitle>
        <DialogContent>
          <Typography fontSize={14} sx={{ mt: 0.5 }}>
            {t('dpv2_down_confirm_body', {
              bumper: downConfirmModal?.bumperNumber ?? '',
            })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const exc = downConfirmModal;
              setDownConfirmModal(null);
              if (exc) {
                setStatusModal({
                  exc,
                  status: 'down',
                  reason: lastStatuses[exc.symbotId]?.payload?.reason ?? '',
                  description:
                    lastStatuses[exc.symbotId]?.payload?.description ?? '',
                });
              }
            }}
            sx={{ textTransform: 'none' }}
          >
            {t('no')}
          </Button>
          <Button
            variant="contained"
            size="small"
            color="error"
            onClick={() => {
              if (downConfirmModal) {
                setConfirmedDown((prev) => {
                  const next = new Set(prev);
                  next.add(downConfirmModal.symbotId);
                  return next;
                });
              }
              setDownConfirmModal(null);
            }}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {t('yes')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─── MultiSelectCell ─────────────────────────────────────────────────────────

interface MultiSelectOption {
  id: string;
  label: string;
  sub: string;
  // optional status pill shown next to the label (e.g. Locked Trucks options)
  statusLabel?: string;
  statusColor?: string;
}

export interface MultiSelectCellProps {
  popId: string;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  selected: string[];
  options: MultiSelectOption[];
  onToggleItem: (id: string) => void;
  placeholder: string;
  enableDarkTheme: boolean;
  borderColor: string;
  textMuted: string;
  textPrimary: string;
  theme: Theme;
  disabled?: boolean;
  // when true, skips the built-in flat chip list below the trigger — for
  // callers that render their own grouped/custom chip layout instead
  hideChips?: boolean;
}

export const MultiSelectCell: React.FC<MultiSelectCellProps> = ({
  popId,
  open,
  onToggle,
  onClose,
  selected,
  options,
  onToggleItem,
  placeholder,
  enableDarkTheme,
  borderColor,
  textMuted,
  textPrimary,
  theme,
  disabled = false,
  hideChips = false,
}) => {
  const { t } = useTranslation();
  const popBg = enableDarkTheme ? '#111a2e' : theme.palette.background.paper;
  const chipBg = alpha(theme.palette.primary.main, 0.14);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const [openUpward, setOpenUpward] = useState(false);
  const [search, setSearch] = useState('');
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const visibleOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...options]
      .sort((a, b) => a.label.localeCompare(b.label))
      .filter((opt) => !q || opt.label.toLowerCase().includes(q));
  }, [options, search]);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  // hover-out (rather than click-outside) closes the dropdown — a short delay
  // lets the mouse cross the small gap between the trigger and the popover
  // without the crossing itself being mistaken for "left the component"
  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      onClose();
    }, 250);
  };

  useEffect(() => clearCloseTimer, []);

  const handleToggle = () => {
    if (disabled) return;
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setOpenUpward(spaceBelow < 260);
    } else {
      setSearch('');
    }
    onToggle();
  };

  return (
    <Box sx={{ position: 'relative', opacity: disabled ? 0.45 : 1 }}>
      {/* trigger button */}
      <Box
        ref={triggerRef}
        data-lock-trigger
        onClick={handleToggle}
        onMouseEnter={clearCloseTimer}
        onMouseLeave={() => {
          if (open) scheduleClose();
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: `1px solid ${borderColor}`,
          borderRadius: 1,
          px: 1,
          py: 0.75,
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontSize: 11.5,
          color: textMuted,
          minHeight: 32,
          ...(!disabled && {
            '&:hover': { borderColor: theme.palette.primary.main },
          }),
        }}
      >
        <span>
          {selected.length
            ? `${selected.length} ${
                selected.length === 1 ? 'locked' : 'locked'
              }`
            : placeholder}
        </span>
        <span style={{ fontSize: 10, marginLeft: 6 }}>▾</span>
      </Box>

      {/* chips */}
      {!hideChips && selected.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
          {selected.map((id) => (
            <Chip
              key={id}
              label={id}
              size="small"
              disabled={disabled}
              onDelete={
                disabled
                  ? undefined
                  : () => {
                      onToggleItem(id);
                    }
              }
              sx={{
                fontSize: 10,
                height: 20,
                bgcolor: chipBg,
                color: theme.palette.primary.main,
              }}
            />
          ))}
        </Box>
      )}

      {/* dropdown */}
      <Popper
        open={open}
        anchorEl={triggerRef.current}
        placement={openUpward ? 'top-start' : 'bottom-start'}
        sx={{ zIndex: (t) => t.zIndex.modal + 1 }}
        modifiers={[
          { name: 'preventOverflow', options: { padding: 8 } },
          { name: 'flip', enabled: true },
        ]}
      >
        <Paper
          id={popId}
          data-lock-popover
          onMouseEnter={clearCloseTimer}
          onMouseLeave={() => {
            if (open) scheduleClose();
          }}
          sx={{
            mt: openUpward ? 0 : 0.5,
            mb: openUpward ? 0.5 : 0,
            bgcolor: popBg,
            border: `1px solid ${borderColor}`,
            borderRadius: 1.5,
            boxShadow: '0 6px 24px rgba(0,0,0,0.4)',
            minWidth: 220,
            maxHeight: 240,
            overflowY: 'auto',
            p: 0.5,
          }}
        >
          {options.length > 0 && (
            <TextField
              size="small"
              autoFocus
              fullWidth
              placeholder={t('dpv2_search_options')}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
              sx={{
                mb: 0.5,
                '& .MuiInputBase-input': { fontSize: 12, py: 0.75 },
              }}
            />
          )}
          {visibleOptions.length === 0 && (
            <Box sx={{ px: 1.5, py: 1, fontSize: 12, color: textMuted }}>—</Box>
          )}
          {visibleOptions.map((opt) => {
            const isOn = selected.includes(opt.id);
            return (
              <Box
                key={opt.id}
                onClick={() => {
                  onToggleItem(opt.id);
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.25,
                  py: 0.875,
                  borderRadius: 1,
                  cursor: 'pointer',
                  fontSize: 12,
                  bgcolor: isOn
                    ? alpha(theme.palette.primary.main, 0.1)
                    : 'transparent',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <Checkbox
                  size="small"
                  checked={isOn}
                  readOnly
                  sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 16 } }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    fontSize={12}
                    fontWeight={600}
                    sx={{ color: textPrimary }}
                  >
                    {opt.label}
                  </Typography>
                  {opt.statusLabel && (
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: opt.statusColor,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        fontSize={10}
                        fontWeight={700}
                        sx={{ color: opt.statusColor, letterSpacing: '0.02em' }}
                      >
                        {opt.statusLabel}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Paper>
      </Popper>
    </Box>
  );
};

// ─── Export ───────────────────────────────────────────────────────────────────

const DispatchPlanV2View: React.FC = () => (
  <CustomMuiThemeProvider>
    <DispatchPlanV2Inner />
  </CustomMuiThemeProvider>
);

export default DispatchPlanV2View;
